import { ref } from 'vue';

/**
 * Busca por digitação + navegação por teclado para dropdowns customizados.
 * Extraído do padrão usado em TaskFilters.vue (makeKeydownHandler) para ser
 * reutilizado por FlowSelect, AutomationSelect, TagsInput e afins, que hoje
 * reimplementam esta mesma lógica de forma independente.
 *
 * @param {import('vue').Ref<Array>} items - lista de itens navegáveis (já filtrada)
 * @param {Function} onSelect - chamado com o item destacado quando Enter é pressionado
 * @param {Function} onClose - chamado quando Escape é pressionado
 * @returns {{ search: import('vue').Ref<string>, highlightedIndex: import('vue').Ref<number>, handleKeydown: Function, resetHighlight: Function }}
 */
export function useSearchableDropdown({ items, onSelect, onClose }) {
  const search = ref('');
  const highlightedIndex = ref(0);

  function resetHighlight() {
    highlightedIndex.value = 0;
  }

  function moveHighlight(delta) {
    const max = items.value.length - 1;
    highlightedIndex.value = Math.min(
      Math.max(highlightedIndex.value + delta, 0),
      max < 0 ? 0 : max
    );
  }

  function handleKeydown(event) {
    if (event.key === 'Escape') {
      search.value = '';
      onClose();
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      moveHighlight(1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      moveHighlight(-1);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const item = items.value[highlightedIndex.value];
      if (item) onSelect(item);
    } else if (event.key === 'Backspace') {
      search.value = search.value.slice(0, -1);
      resetHighlight();
    } else if (
      event.key.length === 1 &&
      !event.ctrlKey &&
      !event.metaKey &&
      !event.altKey
    ) {
      search.value += event.key;
      resetHighlight();
    }
  }

  return { search, highlightedIndex, handleKeydown, resetHighlight };
}
